import { z } from 'zod';

/**
 * Result type for form actions
 */
export interface ActionResult<T = unknown> {
  success?: boolean;
  error?: string;
  errors?: Record<string, string[]>;
  data?: T;
}

/**
 * Create a basic form action handler
 * @param handler - The async function to handle form data
 * @returns A function compatible with useActionState
 */
export function createFormAction<T>(
  handler: (data: T) => Promise<ActionResult>
) {
  return async (prevState: ActionResult, formData: FormData): Promise<ActionResult> => {
    try {
      // Convert FormData to object - safe conversion
      const formEntries = Object.fromEntries(formData);
      const data = formEntries as T;
      
      // Call the handler
      const result = await handler(data);
      
      if (result.error) {
        return { error: result.error };
      }
      
      return { success: true, data: result.data };
    } catch (error) {
      console.error('Form action error:', error);
      return { 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    }
  };
}

/**
 * Create a form action with Zod validation
 * Enhanced for complex forms like TransactionForm
 * @param schema - Zod schema for validation
 * @param handler - The async function to handle validated data
 * @returns A function compatible with useActionState
 */
export function createValidatedFormAction<T>(
  schema: z.ZodSchema<T>,
  handler: (data: T) => Promise<ActionResult>
) {
  return async (prevState: ActionResult, formData: FormData): Promise<ActionResult> => {
    try {
      // Convert FormData to object, handling complex types
      const rawData: Record<string, unknown> = {};
      
      for (const [key, value] of formData.entries()) {
        // Handle empty strings as undefined for optional fields
        if (value === '') {
          rawData[key] = undefined;
          continue;
        }
        
        // Handle numeric fields
        // 🛑 REMOVED: amount field conversion to number - schema expects string
        // Amount validation is handled by Zod schema, not here
        
        // Handle boolean fields (checkboxes)
        if (key === 'recurring') {
          rawData[key] = value === 'true' || value === 'on';
          continue;
        }
        
        // Handle date fields - keep as string but validate format
        if ((key === 'date' || key === 'endDate') && typeof value === 'string') {
          rawData[key] = value || undefined;
          continue;
        }
        
        // Handle frequency enum values
        if (key === 'frequency' && typeof value === 'string') {
          rawData[key] = value || undefined;
          continue;
        }
        
        // Default case
        rawData[key] = value;
      }
      
      // Parse with Zod
      const parsed = schema.safeParse(rawData);
      
      if (!parsed.success) {
        // Format Zod errors for user-friendly display
        const errors = parsed.error.flatten().fieldErrors;
        const formattedErrors: Record<string, string[]> = {};
        
        for (const [key, messages] of Object.entries(errors)) {
          if (Array.isArray(messages)) {
            formattedErrors[key] = messages;
          } else if (typeof messages === 'string') {
            formattedErrors[key] = [messages];
          }
        }
        
        // Get the first error for the main error message
        const firstIssue = parsed.error.issues[0];
        if (firstIssue) {
          const fieldName = firstIssue.path.length > 0 ? firstIssue.path.join('.') : 'form';
          const errorMessage = `${fieldName}: ${firstIssue.message}`;
          return { 
            errors: formattedErrors,
            error: errorMessage
          };
        }
        
        return { 
          errors: formattedErrors,
          error: 'Validation failed'
        };
      }
      
      // Call the handler with validated data
      const result = await handler(parsed.data);
      
      if (result.error) {
        return { error: result.error };
      }
      
      return { success: true, data: result.data };
    } catch (error) {
      console.error('Form action error:', error);
      return { 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    }
  };
}

/**
 * Helper to extract form data with type safety
 * @param formData - FormData object
 * @param fields - Array of field names to extract
 * @returns Object with extracted values
 */
export function extractFormData<T extends Record<string, unknown>>(
  formData: FormData,
  fields: (keyof T)[]
): Partial<T> {
  const result: Partial<T> = {};
  
  for (const field of fields) {
    const fieldName = String(field);
    const value = formData.get(fieldName);
    if (value !== null) {
      // Safe assignment - FormData.get() returns FormDataEntryValue which is string | File
      result[field] = value as T[keyof T];
    }
  }
  
  return result;
}

/**
 * Convert FormData to a typed object, handling nested fields and arrays
 * @param formData - FormData object
 * @returns Parsed object
 */
export function parseFormData<T = Record<string, unknown>>(formData: FormData): T {
  const obj: Record<string, unknown> = {};
  
  for (const [key, value] of formData.entries()) {
    // Handle nested fields (e.g., "user.name" -> { user: { name: value } })
    const keys = key.split('.');
    let current = obj;
    
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      if (!k) continue;
      if (!(k in current)) {
        current[k] = {};
      }
      const nested = current[k];
      if (typeof nested === 'object' && nested !== null) {
        current = nested as Record<string, unknown>;
      } else {
        // Reset to empty object if not object type
        current[k] = {};
        current = current[k] as Record<string, unknown>;
      }
    }
    
    const lastKey = keys[keys.length - 1];
    if (!lastKey) continue;
    
    // Handle array notation (e.g., "items[0]" -> { items: [value] })
    const arrayMatch = lastKey.match(/^(.+)\[(\d+)\]$/);
    if (arrayMatch) {
      const [, arrayKey, index] = arrayMatch;
      if (!arrayKey || !index) continue;
      if (!(arrayKey in current)) {
        current[arrayKey] = [];
      }
      const array = current[arrayKey];
      if (Array.isArray(array)) {
        const indexNum = parseInt(index);
        if (!isNaN(indexNum) && indexNum >= 0) {
          array[indexNum] = value;
        }
      }
    } else {
      current[lastKey] = value;
    }
  }
  
  return obj as T;
}